import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend,
  BarElement,
} from 'chart.js';
import './ResponseQuality.css';
import debounce from '../../utils/Debounce';
import LoadingChart from '../../components/LoadingChart';
import Select, { components } from 'react-select';
import { Tooltip as ReactTooltip } from 'react-tooltip'
import axiosCMS from '../../libs/axios/axiosCMS';

ChartJS.register(CategoryScale, LinearScale, PointElement, BarElement, Title, Tooltip, Legend);

const RQLineChart = ({ chartWidth, selectedFilters, selectedMonth, selectedYear }) => {
  const [selectedMetric, setSelectedMetric] = useState({ value: 'success_rate', label: 'Success Total' });
  const [selectedTime, setSelectedTime] = useState({ value: 'day', label: 'Daily' });
  const [chartLegend, setChartLegen] = useState('Success Total')
  const [data, setData] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSelectDayDisabled, setIsSelectDayDisabled] = useState(false)

  const listMetrics = [
    { value: 'success_rate', label: 'Success Total' },
    { value: 'fallback_rate', label: 'Fallback Total' },
    { value: 'satisfaction_score', label: 'Satisfaction Score' },
    { value: 'user_rating', label: 'Avg User Rating' },
    { value: 'response_time', label: 'Avg Response Time (s)' },
  ]

  const listTime = [
    { value: 'day', label: 'Daily' },
    { value: 'month', label: 'Monthly' },
    { value: 'year', label: 'Yearly' },
  ]

  /**
   * Handles the change of the selected metric.
   * Updates the chart legend and selected metric state.
   */
  const handleMetricChange = (selectedOptions) => {
    setChartLegen(selectedOptions.label)
    setSelectedMetric(selectedOptions);
  };

  /**
   * Handles the change of the selected time period.
   * Updates the selected time state.
   * @param {*} selectedOptions 
   */
  const handleTimeChange = (selectedOptions) => {
    setSelectedTime(selectedOptions)
  };

  /**
   * Fetches line chart data based on selected filters, metric, and time period.
   * Uses debounce to limit the frequency of API calls.
   * @param {Object} filters - The filters to apply when fetching data.
   * @param {Object} metric - The selected metric for the chart.
   * @param {Object} time - The selected time period for the chart.
   */
  const getData = useCallback(debounce(async (filters, metric, time) => {
    setIsLoading(true)
    try {
      const url = "response-quality/line-chart";
      const response = await axiosCMS.post(url, {
        ...filters,
        line_type: metric.value,
        period_type: time.value
      });
      if (response.status == 200) {
        const result = await response.data;
        setData(result.data)
      }
    } catch (error) {
      console.error('Error fetching chart data:', error);
    }
    setIsLoading(false)
  }, 1500), [])

  useEffect(() => {
    getData(selectedFilters, selectedMetric, selectedTime);
  }, [selectedFilters, selectedMetric, selectedTime])

  useEffect(() => {
    if (selectedMonth.length > 1 || selectedYear.length > 1) {
      setIsSelectDayDisabled(true)
      setSelectedTime({ value: 'month', label: 'Monthly' })
    } else {
      setIsSelectDayDisabled(false)
    }
  }, [selectedMonth, selectedYear])

  const chartData = {
    labels: data.map((x) => x.date),
    datasets: [
      {
        data: data.map((x) => x.total),
        borderColor: 'white',
        backgroundColor: '#71AEEB',
        borderWidth: 2,
        maxBarThickness: 70,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      }
    },
    scales: {
      y: {
        suggestedMin: 0,

        // the data maximum used for determining the ticks is Math.max(dataMax, suggestedMax)
        suggestedMax: 5,
      }
    },
  };

  const CostumOption = ({ innerProps, ...props }) => {
    return (
      <>
        <components.Option {...props} innerProps={innerProps} className={props.isDisabled ? 'my-anchor-element-id' : ''} />
        <ReactTooltip id='tooltip' place="right" content='Daily data can be displayed only if one month and year are selected' anchorSelect=".my-anchor-element-id" style={{ maxWidth: "200px" }} />
      </>
    )
  }

  return (
    <div className='rq-section grow'>
      <div className='group-flex-8 justify-between mb-16'>
        <Select
          id="metricType"
          name="metricType"
          isSearchable={false}
          options={listMetrics}
          value={selectedMetric}
          onChange={handleMetricChange}
          placeholder={"-- Select Metric --"}
          classNamePrefix="select"
          className='w-200'
        />
        <Select
          id="periodType"
          name="periodType"
          isSearchable={false}
          components={{ Option: CostumOption }}
          options={listTime}
          value={selectedTime}
          onChange={handleTimeChange}
          placeholder={"-- Select Period --"}
          classNamePrefix="select-period-chart"
          isOptionDisabled={(option) => option.value === 'day' && isSelectDayDisabled}
        />
      </div>
      <div className='rq-chart-container' style={{ position: "relative", width: chartWidth, height: '300px' }}>
        {isLoading &&
          <LoadingChart />
        }
        <Bar
          data={chartData}
          options={chartOptions}
        />
      </div>
      <div className='chart-legend-container'>
        {
          chartLegend &&
          <div className='chart-legend'>
            <div className='legend-blue'></div>
            <p className='legend-title'>
              {chartLegend}
            </p>
          </div>
        }
      </div>
    </div>
  );
};

export default RQLineChart;
